import moment from 'moment';

export type ExpirationMode = 'Default' | 'Calculated' | 'Manual';

/**
 * Computes expiration date by adding month/day offsets to the current expiration date.
 * Returns null if no valid expirationDate or both offsets are empty.
 */
export function computeExpirationByOffsets(params: {
  months: string | number | null | undefined;
  days: string | number | null | undefined;
  expirationDate?: string | null;
}): string | null {
  const { months, days, expirationDate } = params;
  const m = months !== '' && months != null ? Number(months) : NaN;
  const d = days !== '' && days != null ? Number(days) : NaN;

  if (isNaN(m) && isNaN(d)) return null;
  if (!expirationDate) return null;

  const result = moment(expirationDate);
  if (!isNaN(m)) result.add(m, 'months');
  if (!isNaN(d)) result.add(d, 'days');
  return result.format('YYYY-MM-DD');
}

/** Default expiration = grantDate + optionExpirationInYears (falls back to 10 years) */
export function computeDefaultExpiration(
  grantDate: string | null | undefined,
  optionExpirationInYears?: number | null
): string | null {
  if (!grantDate || !optionExpirationInYears) return null;
  return moment(grantDate).add(optionExpirationInYears, 'years').format('YYYY-MM-DD');
}

/** Determines if both offset fields are empty/null */
export function areOffsetsEmpty(
  months: string | number | null | undefined,
  days: string | number | null | undefined
): boolean {
  return (months === '' || months == null) && (days === '' || days == null);
}

/** Returns true if the award type is an option (not RSU or PSU) */
export function isOptionAwardType(awardType: string | null | undefined): boolean {
  return awardType !== 'RSU' && awardType !== 'PSU';
}

export interface ExpirationOffsetChangeParams {
  field: 'optionExpirationInDays' | 'optionExpirationInMonths';
  value: string;
  rowIndex: number;
  awardId: string;
  initialAwardDetails: any[];
  setFieldValue: (field: string, value: any) => void;
  setInitialData: (data: any) => void;
  initialData: any;
  selectedTableData: any[];
  setSelectedTableData: (data: any[]) => void;
  setExpirationModeForAward?: (awardId: string, mode: ExpirationMode) => void;
  terminationDate?: string | null;
}

/**
 * Shared handler for Expiration Days / Expiration Months onBlur in the termination table.
 * Both columns have identical logic aside from which offset field is being changed.
 */
export function handleExpirationOffsetChange(params: ExpirationOffsetChangeParams): void {
  const {
    field,
    value,
    rowIndex,
    awardId,
    initialAwardDetails,
    setFieldValue,
    setInitialData,
    initialData,
    selectedTableData,
    setSelectedTableData,
    setExpirationModeForAward,
    terminationDate,
  } = params;

  const currentValue = initialAwardDetails?.[rowIndex]?.[field];
  const currentStr = currentValue != null ? String(currentValue) : '';
  const newStr = value != null ? String(value).trim() : '';
  if (currentStr === newStr) return;

  const isEmpty = value === '' || value == null;
  const parsedValue = !isEmpty ? Number(value) : null;
  setFieldValue(`awardDetails[${rowIndex}].${field}`, parsedValue);

  const counterField =
    field === 'optionExpirationInDays' ? 'optionExpirationInMonths' : 'optionExpirationInDays';
  const counterValue = initialAwardDetails?.[rowIndex]?.[counterField];

  const awardBaseDate =
    initialAwardDetails?.[rowIndex]?.baseExpirationDate ??
    initialAwardDetails?.[rowIndex]?.expirationDate;

  const months = field === 'optionExpirationInMonths' ? value : counterValue;
  const days = field === 'optionExpirationInDays' ? value : counterValue;

  const calcBase = terminationDate ?? awardBaseDate;
  const newExpiry = computeExpirationByOffsets({ months, days, expirationDate: calcBase });
  const resolvedExpiry = newExpiry ?? terminationDate ?? awardBaseDate;

  setExpirationModeForAward?.(awardId, newExpiry ? 'Calculated' : 'Default');

  const updatedAwardDetails = initialAwardDetails?.map((item: any, index: number) =>
    index === rowIndex
      ? { ...item, [field]: !isEmpty ? value : null, expirationDate: resolvedExpiry }
      : item
  );
  setInitialData({ ...initialData, awardDetails: updatedAwardDetails });

  const updatedSelectedTableData = selectedTableData?.map((item: any) =>
    item.awardId === awardId
      ? { ...item, [field]: !isEmpty ? value : null, expirationDate: resolvedExpiry }
      : item
  );
  setSelectedTableData?.(updatedSelectedTableData);
}
